import { Locale } from "@/Common/Models/Localization/Locale";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { OntarioObstetricalFilePage, OntarioObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/OntarioObstetricalFilePage";
import { BloodPressureField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/BloodPressureField";
import { ObstetricalFileField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalFileField";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { BloodPressure } from "@/Patient/Models/MedicalSummary/BloodPressure";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { WeightUnitComponent } from "../../../Common/Subcomponent/WeightUnitComponent";
import { OntarioObstetricalFilePostnatalDiscussions } from "./Subcomponent/OntarioObstetricalFilePostnatalDiscussions";
import { OntarioObstetricalFilePostnatalHeader } from "./Subcomponent/OntarioObstetricalFilePostnatalHeader";
import { OntarioObstetricalFilePostnatalHistoryImmunizationAndConsumables } from "./Subcomponent/OntarioObstetricalFilePostnatalHistoryImmunizationAndConsumables";
import { OntarioObstetricalFilePostnatalHistoryInfant } from "./Subcomponent/OntarioObstetricalFilePostnatalHistoryInfant";
import { OntarioObstetricalFilePostnatalHistoryPatientStatus } from "./Subcomponent/OntarioObstetricalFilePostnatalHistoryPatientStatus";
import { OntarioObstetricalFilePostnatalPhysicalExamResultState } from "./Subcomponent/OntarioObstetricalFilePostnatalPhysicalExamResultState";

class OntarioObstetricalFilePostnatalState extends OntarioObstetricalFilePageState {
    public vitalSign: ObstetricalFileVitalSign;
    public isExamCommentsClickableTextVisible: boolean = false;
    public heightInMeters: number;
}

class OntarioObstetricalFilePostnatal extends OntarioObstetricalFilePage<OntarioObstetricalFilePostnatalState> {
    private readonly componentId = "ontario-obstetrical-file-Postnatal";

    protected initState(props: IObstetricalFilePageProps<OntarioObstetricalFileForm>): void {
        this.mutateState(
            s => {
                s.form = new OntarioObstetricalFileForm(props.form);
                s.vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
                    emrTemplateId: s.form.Postnatal.PhysicalExamAsIndicated.EmrTemplateId,
                    consultationDate: props.encounter.ConsultationDate,
                    obstetricalFileContributionDate: props.contributionCreationDate
                });

                s.form.Postnatal.DateOfVisit ??= props.encounter.ConsultationDate;
                s.form.Postnatal.History.LastPapTestDate ??= s.form.Opr2.LastPapDate;
                s.form.Postnatal.History.LastPaptestResult ??= s.form.Opr2.LastPapResult;
                s.form.Postnatal.History.TaceScreeningScore ??= s.form.Resources.TaceScreening.TotalScore;
                s.form.Postnatal.PhysicalExamAsIndicated.EmrTemplateId = s.vitalSign?.emrTemplateId;
                s.form.Postnatal.History.BirthAttendant ??= s.form.Common.PlannedBirthAttendant;

                s.form.setSubsequentVisitsVitalSigns(
                    props.obstetricalFileMedicalSummary
                    , props.encounter.ConsultationDate
                    , props.contributionCreationDate
                    , props.contributionId
                );

                s.form.Postnatal.PhysicalExamAsIndicated.PreDeliveryWeight ??= [...s.form.Opr3.SubsequentVisits]
                    ?.sortByPropertyDesc(p => p.Date)
                    .firstOrDefault()
                    ?.WeightInKg;

                s.heightInMeters = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
                    emrTemplateId: s.form.Opr2.VitalSignEmrTemplateId,
                    consultationDate: props.encounter.ConsultationDate,
                    obstetricalFileContributionDate: props.contributionCreationDate,
                    skipLoadLastHour: !!s.form.Opr2.VitalSignEmrTemplateId
                })?.heightInMeters;
            },
            () => {
                if (this.state.form.Postnatal.PhysicalExamAsIndicated.EmrTemplateId)
                    this.handleObstetricalFileVitalSignLink(this.state.form.Postnatal.PhysicalExamAsIndicated.EmrTemplateId);
            }
        );
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId} className="inner-border">
                <OntarioObstetricalFilePostnatalHeader
                    {...this.getSubcomponentBaseProps()}
                    patientLastName={this.renderLastName("col-md-3")}
                    patientFirstName={this.renderFirstName("col-md-3")}
                />
                <div className="section">
                    {this.renderSectionTitleRow("HistoryLabel")}
                    <OntarioObstetricalFilePostnatalHistoryInfant {...this.getSubcomponentBaseProps()} />
                    <OntarioObstetricalFilePostnatalHistoryPatientStatus {...this.getSubcomponentBaseProps()} />
                    <OntarioObstetricalFilePostnatalHistoryImmunizationAndConsumables {...this.getSubcomponentBaseProps()} />
                </div>
                <div className="section border border-top">
                    {this.renderSectionTitleRow("PhysicalExamAsIndicatedLabel")}
                    {this.renderPhysicalExamSection()}
                </div>
                <OntarioObstetricalFilePostnatalDiscussions
                    {...this.getSubcomponentBaseProps()}
                />
                {this.renderSignature()}
            </div>
        );
    }

    protected async onVitalSignUpdated(): Promise<void> {
        let updatedVitalSign = await super.handleVitalSignUpdate(
            this.state.vitalSign,
            this.state.form.Postnatal.PhysicalExamAsIndicated.EmrTemplateId
        );

        this.mutateState(
            s => {
                s.vitalSign.updateValueFromMedicalItemVitalSign(updatedVitalSign);
                s.form.Postnatal.PhysicalExamAsIndicated.EmrTemplateId = updatedVitalSign?.EMRTemplateId;
            },
            () => this.handleObstetricalFileVitalSignLink(updatedVitalSign?.EMRTemplateId)
        );
    }

    private renderPhysicalExamSection(): JSX.Element {
        const physicalExam = this.state.form.Postnatal.PhysicalExamAsIndicated;
        const commonData = this.state.form.Common;
        const shouldDisplayPreDeliveryWeightUnit = physicalExam.PreDeliveryWeight || !this.props.readonly;
        const shouldDisplayWeightUnit = this.state.vitalSign?.weightInKgs || !this.props.readonly;
        const shouldDisplayPrePregnancyWeightUnit = commonData?.PrePregnancyWeight || !this.props.readonly;
        const showBloodPressureUnit = !this.props.readonly || this.state.vitalSign?.bloodPressure.hasValue();
        const isLatestMedicalItemInEmrTemplate = this.props.obstetricalFileMedicalSummary.isLatestMedicalItemInEmrTemplate(this.state.vitalSign);

        return (
            <div>
                <div className="row row-form">
                    <div className="col-md-2">
                        <ObstetricalFileField
                            label="WeightTodayLabel"
                            field={this.vitalSignControlRenderer.renderTextbox({
                                value: this.state.vitalSign.getWeightIn(physicalExam.WeightTodayUnit, 2)?.toString(),
                                isReadOnlyOverride: !isLatestMedicalItemInEmrTemplate,
                                onChange: value => this.mutateModelState(s => {
                                    s.vitalSign.weightInKgs = value && WeightUnitConverter.convert(physicalExam.WeightTodayUnit, WeightUnit.Kilogram, parseFloat(value));
                                }),
                                maxLength: 8,
                                isNumeric: true,
                                className: `${this.state.vitalSign?.emrIsInvalid ? "invalid-vital-sign" : ""}`
                            })}
                            className="flex-column"
                            unit={shouldDisplayWeightUnit && (
                                <WeightUnitComponent
                                    units={[WeightUnit.Kilogram, WeightUnit.Pound]}
                                    selectedUnit={physicalExam.WeightTodayUnit}
                                    onUnitChange={unit => this.mutateModelStateWithFormUpdate(s => {
                                        s.form.Postnatal.PhysicalExamAsIndicated.WeightTodayUnit = unit;
                                        s.vitalSign.weightUnit = unit;
                                    })}
                                    readonly={this.props.readonly}
                                    acceptableLocales={[Locale.en]}
                                />
                            )}
                            inlineFlexValue
                        />
                    </div>
                    <div className="col-md-2">
                        <ObstetricalFileField
                            label="PreDeliveryWeightLabel"
                            field={this.controlRenderer.renderTextbox({
                                value: physicalExam.ConvertedPreDeliveryWeight?.toString(),
                                onChange: value => this.mutateModelState(s => {
                                    s.form.Postnatal.PhysicalExamAsIndicated.ConvertedPreDeliveryWeight = value && parseFloat(value);
                                }),
                                maxLength: 8,
                                isNumeric: true
                            })}
                            className="flex-column"
                            unit={shouldDisplayPreDeliveryWeightUnit && (
                                <WeightUnitComponent
                                    units={[WeightUnit.Kilogram, WeightUnit.Pound]}
                                    selectedUnit={physicalExam.PreDeliveryWeightUnit}
                                    onUnitChange={unit => this.mutateModelStateWithFormUpdate(s => s.form.Postnatal.PhysicalExamAsIndicated.PreDeliveryWeightUnit = unit)}
                                    readonly={this.props.readonly}
                                    acceptableLocales={[Locale.en]}
                                />
                            )}
                            inlineFlexValue
                        />
                    </div>
                    <div className="col-md-2">
                        <ObstetricalFileField
                            label="PrePregnancyWeightLabel"
                            field={this.controlRenderer.renderTextbox({
                                value: commonData.ConvertedPrePregnancyWeight?.toString(),
                                onChange: value => this.handlePrePregnancyWeightChange(value),
                                maxLength: 8,
                                isNumeric: true
                            })}
                            className="flex-column"
                            unit={shouldDisplayPrePregnancyWeightUnit && (
                                <WeightUnitComponent
                                    units={[WeightUnit.Kilogram, WeightUnit.Pound]}
                                    selectedUnit={this.state.form.Common.PrePregnancyWeightUnit}
                                    onUnitChange={unit => this.mutateModelStateWithFormUpdate(s => s.form.Common.PrePregnancyWeightUnit = unit)}
                                    readonly={this.props.readonly}
                                    acceptableLocales={[Locale.en]}
                                />
                            )}
                            inlineFlexValue
                        />
                    </div>
                    <div className="col-md-3">
                        {this.renderField(
                            "BloodPressureAbbrevLabel",
                            <BloodPressureField
                                readonly={this.props.readonly || !isLatestMedicalItemInEmrTemplate}
                                bloodPressure={this.state.vitalSign?.bloodPressure}
                                isInvalid={this.state.vitalSign.emrIsInvalid}
                                onBloodPressureChange={(bloodPressure: BloodPressure) => this.mutateModelState(s => s.vitalSign.bloodPressure = bloodPressure)}
                                onBlur={() => this.onVitalSignUpdated()}
                            />,
                            "flex-column",
                            showBloodPressureUnit && "mm Hg"
                        )}
                    </div>
                </div>
                <OntarioObstetricalFilePostnatalPhysicalExamResultState {...this.getSubcomponentBaseProps()} />
            </div>
        );
    }

    private renderSignature(): JSX.Element {
        return (
            <div className="section row">
                {this.renderField(
                    "ObstetricalFile_SignatureHealcareProvider",
                    this.controlRenderer.renderTextbox({
                        value: this.state.form.Postnatal.HealthProviderSignature,
                        onChange: value => this.mutateModelState(s => s.form.Postnatal.HealthProviderSignature = value),
                        maxLength: OntarioObstetricalFileFieldLimits.Page5.Postnatal_SignatureOfHealthcareProvider.MaxChars
                    }),
                    "col-xs-6"
                )}
            </div>
        );
    }

    private handlePrePregnancyWeightChange(value: any): void {
        this.mutateModelState(s => {
            s.form.Common.ConvertedPrePregnancyWeight = value && parseFloat(value);

            if (s.form.Common.PrePregnancyWeight && s.heightInMeters) {
                let bodyMassIndex = MedicalCalculator.calculateBodyMassIndex(s.form.Common.PrePregnancyWeight, s.heightInMeters)?.toFixed(2);
                s.form.Common.PrePregnancyBodyMassIndex = bodyMassIndex ? Number(bodyMassIndex) : null;
            }
        });
    }
}

export { OntarioObstetricalFilePostnatal };
